import * as React from 'react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import type { Model } from 'modules/core/lib/mobx-rest'

import ResourceFieldCollection from 'modules/custom_resources/store/collections/customFields/resourceFields'
import CustomFieldValuesCollection from 'modules/custom_resources/store/collections/customFields/values'
import type ResourceField from 'modules/custom_resources/store/models/customFields/ResourceField'
import type { ICustomFieldValue } from 'modules/custom_resources/store/models/customFields/Value'
import type Employee from 'modules/employees/store/models/Employee'

interface resourceFieldsProps {
  usageGroups: string[]
  fieldableModelRef: string
  fieldableId: number
  valuableModelRef: string
  valuableInstance: Model
  employee?: Employee
  resourceFieldFilter?: (models: ResourceField[]) => ResourceField[]
}

export const useResourceField = ({
  usageGroups,
  fieldableModelRef,
  fieldableId,
  valuableModelRef,
  valuableInstance,
  employee,
  resourceFieldFilter,
}: resourceFieldsProps) => {
  const employeeId = employee?.attributes.id || null

  const resourceFieldsCollection = React.useRef(
    new ResourceFieldCollection([], {
      model_ref: fieldableModelRef,
      instance_id: fieldableId,
    })
  ).current

  const valuesCollection = React.useRef(
    new CustomFieldValuesCollection([], {
      model_ref: valuableModelRef,
      instance_id: valuableInstance.id,
    })
  ).current

  const { loading } = useAsyncData(() => {
    valuesCollection.config = {
      model_ref: valuableModelRef,
      instance_id: valuableInstance.id,
    } as any
    resourceFieldsCollection.config = {
      model_ref: fieldableModelRef,
      instance_id: fieldableId.toString(),
    }

    return Promise.all([
      valuesCollection.byUsageGroup(usageGroups),
      resourceFieldsCollection.byUsageGroup(usageGroups, employeeId),
    ])
  }, [
    valuableModelRef,
    valuableInstance.id,
    fieldableModelRef,
    fieldableId,
    JSON.stringify(usageGroups),
    employeeId,
  ])

  const createValue = (value: Partial<ICustomFieldValue>) => valuesCollection.create(value)

  const filterResourceFields = (): ResourceField[] => {
    if (!resourceFieldFilter) return resourceFieldsCollection.models

    return resourceFieldFilter(resourceFieldsCollection.models)
  }

  return {
    loading: loading,
    filteredResourceFields: filterResourceFields(),
    valuesCollection: valuesCollection.models,
    createdValue: createValue,
  }
}
